import React from 'react';

import {
    formatEventDate,
    formatEventTime,
    formatLinkBlockToUrl
} from '$utilities/format.js';

import { IconArrowRight } from '$partials/icon/index.js';
import { isLocalURL } from '$utilities/browser';
import { ModuleDispatcher } from '$partials/modules/index.js';
import { PageShell } from '$partials/global/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').EventDetailPage}
 */
const EventDetailPage = (props) => {
    const page = props.data?.page;

    const content = page?.content || [];
    const ctaHref = formatLinkBlockToUrl(page?.ctaLink);
    const date = formatEventDate(page, props.timezone);
    const isCtaExternal = ctaHref && !isLocalURL(ctaHref);
    const time = formatEventTime(page, props.timezone);

    return (
        <PageShell
            banner
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40 -mb-25'>
                    <div className='layout-grid flex-col sm:flex-row gap-y-38 sm:gap-y-0'>
                        {/* Page header */}
                        <div className='w-full sm:w-4/12 lg:w-3/12 layout-grid-col'>
                            <div className='sm:sticky sm:top-110'>
                                <h1 className='type-style-heading'>
                                    {page?.title}
                                </h1>

                                <div className='mt-16 sm:mt-24'>
                                    {date && (
                                        <p className='type-style-body'>{date}</p>
                                    )}

                                    {time && (
                                        <p className='type-style-body'>{time}</p>
                                    )}

                                    {page?.studio?.title && (
                                        <p className='type-style-body'>{page.studio.title}</p>
                                    )}

                                    {page?.address && (
                                        <p className='type-style-body whitespace-pre-line'>
                                            {page.address}
                                        </p>
                                    )}
                                </div>

                                {(page?.ctaLabel && ctaHref) && (
                                    <p className='mt-24 sm:mt-32'>
                                        <a
                                            className='button type-style-body cursor-pointer'
                                            href={ctaHref}
                                            rel={isCtaExternal ? 'noopener noreferrer' : undefined}
                                            target={isCtaExternal ? '_blank' : undefined}>
                                            {page.ctaLabel}

                                            <span
                                                aria-hidden={true}
                                                className='ml-5'>
                                                <IconArrowRight
                                                    height={20}
                                                    width={20} />
                                            </span>
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* The programme */}
                        <div className='w-full sm:w-8/12 layout-grid-col'>
                            {/* Push heading down */}
                            <div
                                aria-hidden={true}
                                className='hidden sm:block sm:w-4/8 text-transparent type-style-heading select-none pointer-events-none'>
                                {page?.title}
                            </div>

                            <div>
                                {page?.heading && (
                                    <p className='type-style-heading sm:w-5/8'>{page.heading}</p>
                                )}

                                {page?.introduction && (
                                    <div
                                        className='rich-text type-style-body mt-8 sm:mt-10 sm:w-4/8'
                                        dangerouslySetInnerHTML={{ __html: page.introduction }} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Streamfield modules */}
            {content.length > 0 && (
                <div>
                    {content.map((module) => (
                        <ModuleDispatcher
                            key={module.id}
                            modulePaddingClasses='py-24 sm:py-32'
                            { ...module } />
                    ))}
                </div>
            )}
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default EventDetailPage;
